import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../../services/accountingApi';

function ProfitLoss() {
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
    const [profitLossData, setProfitLossData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadProfitLoss();
    }, [fromDate, toDate]);

    const loadProfitLoss = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await accountingApi.getProfitLoss({
                from_date: fromDate,
                to_date: toDate
            });

            if (response.success) {
                setProfitLossData(response.data);
            } else {
                setError(response.message || 'Failed to load profit & loss statement');
            }
        } catch (err) {
            console.error('Error loading profit & loss:', err);
            setError('An error occurred while loading the profit & loss statement');
        } finally {
            setLoading(false);
        }
    };

    const netProfit = (profitLossData?.total_income || 0) - (profitLossData?.total_expenses || 0);

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">Profit & Loss Statement</h1>
                </div>
                <p className="text-sm text-slate-600">Income and expenses summary</p>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">From Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">To Date</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                        />
                    </div>
                </div>
            </div>

            {loading && (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="mt-2 text-slate-600">Loading profit & loss...</p>
                </div>
            )}

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md mb-6">
                    <p className="text-sm text-rose-700">{error}</p>
                </div>
            )}

            {!loading && profitLossData && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Income */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-4">
                            <h2 className="text-lg font-bold text-emerald-900">Income</h2>
                        </div>
                        <div className="p-6">
                            {profitLossData.income && profitLossData.income.length > 0 ? (
                                <div className="space-y-3">
                                    {profitLossData.income.map((item) => (
                                        <div key={item.id} className="flex justify-between items-center py-2 border-b border-slate-100">
                                            <span className="text-sm text-slate-700">{item.name}</span>
                                            <span className="text-sm font-bold text-emerald-600">₹{(item.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    ))}
                                    <div className="flex justify-between items-center pt-3 border-t-2 border-emerald-200">
                                        <span className="font-bold text-emerald-900">Total Income</span>
                                        <span className="text-lg font-bold text-emerald-900">₹{(profitLossData.total_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 italic">No income recorded</p>
                            )}
                        </div>
                    </div>

                    {/* Expenses */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="bg-rose-50 border-b border-rose-100 px-6 py-4">
                            <h2 className="text-lg font-bold text-rose-900">Expenses</h2>
                        </div>
                        <div className="p-6">
                            {profitLossData.expenses && profitLossData.expenses.length > 0 ? (
                                <div className="space-y-3">
                                    {profitLossData.expenses.map((item) => (
                                        <div key={item.id} className="flex justify-between items-center py-2 border-b border-slate-100">
                                            <span className="text-sm text-slate-700">{item.name}</span>
                                            <span className="text-sm font-bold text-rose-600">₹{(item.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                        </div>
                                    ))}
                                    <div className="flex justify-between items-center pt-3 border-t-2 border-rose-200">
                                        <span className="font-bold text-rose-900">Total Expenses</span>
                                        <span className="text-lg font-bold text-rose-900">₹{(profitLossData.total_expenses || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 italic">No expenses recorded</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {!loading && profitLossData && (
                <div className={`mt-6 p-6 rounded-xl border-2 ${netProfit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                    <div className="flex justify-between items-center">
                        <span className={`text-xl font-bold ${netProfit >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                            {netProfit >= 0 ? 'Net Profit' : 'Net Loss'}
                        </span>
                        <span className={`text-2xl font-bold ${netProfit >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                            ₹{Math.abs(netProfit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProfitLoss;
